import type {ChartData} from "@mantine/charts";
import {Bar, BarChart as BarChartRecharts, CartesianGrid, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {getNormalisedYAxisLabelsFromChartSeries} from "~/components/reports/widgets/comboChart";
import type {ChartSeries} from "~/components/reports/widgetSpecific/types";
import {graphColors} from "~/constants/graphColors";
import {graphsXAxisTickFormatter} from "~/components/reports/utilities";import {numberToHumanFriendlyStringWithNumberSystem} from "~/utilities/utilities";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";

export function HorizontalBarChart({
    chartData,
    chartSeries,
    xAxisKey,
    xAxisTitle,
    scalingFactor,
    height,
    maxYValue,
    maxYValueSecondary,
}: {
    chartData: ChartData;
    chartSeries: Array<ChartSeries>;
    xAxisKey: string;
    xAxisTitle: string;
    scalingFactor: number;
    height: number;
    maxYValue: number;
    maxYValueSecondary: number | null;
}) {
    const yAxisLabels = getNormalisedYAxisLabelsFromChartSeries(chartSeries);
    const {theme} = useAnankePreferences();

    const maxLabelLength = 10;

    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            <BarChartRecharts
                data={chartData}
                margin={{top: 20, right: 20, bottom: 20, left: 20}}
                barGap={"6%"}
                maxBarSize={32}
                layout="vertical"
            >
                {yAxisLabels.left !== null && (
                    <XAxis
                        scale="auto"
                        orientation="bottom"
                        xAxisId="left"
                        type="number"
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickMargin={20}
                        tickSize={0}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                    >
                        <Label
                            value={yAxisLabels.left?.join(", ")}
                            dy={28}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </XAxis>
                )}

                {yAxisLabels.right !== null && (
                    <XAxis
                        scale="auto"
                        orientation="top"
                        xAxisId="right"
                        type="number"
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickMargin={20}
                        tickSize={0}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                    >
                        <Label
                            value={yAxisLabels.right?.join(", ")}
                            dy={-30}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </XAxis>
                )}

                <CartesianGrid
                    horizontal={false}
                    vertical={true}
                    stroke={theme === "dark" ? "#ADACAC" : "#262626"}
                    strokeDasharray="7 5"
                />

                <YAxis
                    dataKey={xAxisKey}
                    yAxisId={"left"}
                    orientation={"left"}
                    tickFormatter={(value) => {
                        const formattedValue = graphsXAxisTickFormatter(value);

                        const split = formattedValue.split(" ");
                        if (split[0] !== undefined && split[0].length > 10) {
                            return `${split[0].slice(0, maxLabelLength)}...`;
                        }

                        if (split.join("").length > 10) {
                            return `${split.join("").slice(0, maxLabelLength)}...`;
                        }

                        return formattedValue;
                    }}
                    width={maxLabelLength * 8}
                    tickSize={0}
                    tick={{
                        fontSize: "10px",
                        dx: -2,
                    }}
                    type="category"
                    axisLine={false}
                    // domain={[0, maxYValue]}
                >
                    <Label
                        value={xAxisTitle}
                        dx={maxLabelLength * -4}
                        style={{
                            textAnchor: "middle",
                            fontSize: "10px",
                        }}
                        angle={-90}
                    />
                </YAxis>

                <Tooltip
                    contentStyle={{
                        color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                        background: theme === "dark" ? "#1F1F1F" : "#ffffff",
                        border: theme === "dark" ? "1px solid #383838" : "1px solid #0b120e24",
                        borderRadius: "4px",
                        fontSize: "14px",
                    }}
                    labelFormatter={(value) => {
                        return graphsXAxisTickFormatter(value);
                    }}
                    cursor={{
                        fill: theme === "dark" ? "#2B2B2B" : "#E5E5E5",
                    }}
                    formatter={(value) => {
                        const number = parseFloat(value.toString());
                        return Number.isInteger(number) === true ? number : number.toFixed(2);
                    }}
                />

                <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{
                        top: "-4px",
                        fontSize: "10px",
                    }}
                />

                {chartSeries.map((series, seriesIndex) => {
                    return (
                        <Bar
                            dataKey={series.name}
                            fill={graphColors[seriesIndex % graphColors.length]}
                            key={seriesIndex}
                            xAxisId={series.yAxisId}
                            yAxisId={"left"}
                            orientation="vertical"
                        />
                    );
                })}
            </BarChartRecharts>
        </ResponsiveContainer>
    );
}
